import React from 'react';
import type { DateRange } from '../types';
import type moment from 'moment';
import { useCalendar } from './useCalendar';
import { CalendarPrimitives } from './CalendarPrimitives';

interface CalendarProps {
  /**
   * UTC Date represented as a Unix Timestamp
   */
  value?: number;
  /**
   * Maximum date range
   */
  range?: DateRange;
  /**
   * Force 6 rows in the calendar (so two calendars side-by-side are
   * certain to display 6 rows each)
   */
  long?: boolean;
  className?: string;
  /**
   * Triggered on user selecting a date. Returns a UTC Unix Timestamp
   */
  onChange: (date: number) => void;
}

export const Calendar = ({ value, range, long, className, onChange }: CalendarProps) => {
  const { cells, visibleMonth, handlePreviousMonth, handleNextMonth, handleSelect, isDisabled, valueDate } =
    useCalendar({
      value,
      long,
      onChange,
      range,
    });
  return (
    <CalendarPrimitives.Root className={className}>
      <CalendarPrimitives.Header>
        <CalendarPrimitives.PreviousMonthButton
          handlePreviousMonth={handlePreviousMonth}
          variant={{
            color: 'default',
          }}
          disabled={false}
        />
        <CalendarPrimitives.CurrentMonth visibleMonth={visibleMonth} variant={{ color: 'default' }} />
        <CalendarPrimitives.NextMonthButton
          handleNextMonth={handleNextMonth}
          variant={{
            color: 'default',
          }}
          disabled={false}
        />
      </CalendarPrimitives.Header>
      <CalendarPrimitives.DayRow variant={{ size: 'small', color: 'light' }} />
      {cells.map((row: moment.Moment[], i: number) => (
        // eslint-disable-next-line react/no-array-index-key
        <CalendarPrimitives.Row key={String(i)}>
          {row.map((date) => (
            <CalendarPrimitives.Cell
              key={date.valueOf()}
              date={date}
              visibleMonth={visibleMonth}
              valueDate={valueDate}
              handleSelect={handleSelect}
              isDisabled={isDisabled}
              variant={{ color: 'default', type: 'value' }}
            >
              {date.date()}
            </CalendarPrimitives.Cell>
          ))}
        </CalendarPrimitives.Row>
      ))}
    </CalendarPrimitives.Root>
  );
};

export default Calendar;
